import React from 'react';
import { Link, useParams } from 'react-router-dom';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import LandingFooter from '../../components/LandingFooter/LandingFooter';
import Seo, { SITE_URL, SITE_NAME } from '../../components/Seo/Seo';
import Icon from '../../components/ui/Icon';
import { ArticleView, NewsGrid } from './newsComponents';
import { usePublishedPost, usePublishedPosts } from './useNews';
import { postSummary } from '../../utils/posts';
import './Landing.css';
import './News.css';

export function articleTitle(post) {
  return `${post.title} | ${SITE_NAME} News`;
}

function articleJsonLd(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    description: postSummary(post),
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    mainEntityOfPage: `${SITE_URL}/news/${post.slug}`,
    ...(post.cover_image_url ? { image: [post.cover_image_url] } : {}),
    author: post.author
      ? { '@type': 'Person', name: post.author }
      : { '@type': 'Organization', name: SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };
}

function MoreNews({ currentSlug, path }) {
  const { posts } = usePublishedPosts(path, { limit: 4 });
  const others = posts.filter((p) => p.slug !== currentSlug).slice(0, 3);
  if (others.length === 0) return null;
  return (
    <section className="news-related" aria-labelledby="more-news-heading">
      <div className="news-section">
        <div className="news-section-header">
          <h2 id="more-news-heading" className="news-section-title">
            More news
          </h2>
          <Link to="/news" className="news-all-link">
            View all news <Icon name="arrow_forward" aria-hidden="true" />
          </Link>
        </div>
        <NewsGrid posts={others} />
      </div>
    </section>
  );
}

export default function NewsArticle() {
  const { slug } = useParams();
  const { post, loading } = usePublishedPost(slug);
  const path = `/news/${slug}`;

  let content;
  if (post) {
    content = (
      <>
        <Seo
          title={articleTitle(post)}
          description={postSummary(post, 160)}
          path={path}
          jsonLd={articleJsonLd(post)}
        />
        <ArticleView post={post} />
      </>
    );
  } else if (loading) {
    content = (
      <div className="news-article" aria-busy="true" aria-label="Loading article">
        <div className="news-skeleton" />
      </div>
    );
  } else {
    content = (
      <div className="news-article">
        <Seo title={`Article not found | ${SITE_NAME}`} path={path} noindex />
        <div className="news-empty">
          <h2>We couldn’t find that article</h2>
          <p>It may have been moved or unpublished.</p>
          <p>
            <Link to="/news" className="news-all-link">
              Browse all news <Icon name="arrow_forward" aria-hidden="true" />
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <LandingHeader />
      <main id="main" tabIndex={-1}>
        {content}
        {post && <MoreNews currentSlug={slug} path={path} />}
      </main>
      <LandingFooter />
    </div>
  );
}
